import { api } from '../features/auth/authStore';
import axios from 'axios';

// Create a public API instance for unauthenticated requests
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:4000/api/v1';
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

export interface FeatureFlags {
  eSign: boolean;
  payroll: boolean;
  biometricImport: boolean;
  advancedReporting: boolean;
  apiAccess: boolean;
  multiCurrency: boolean;
  customBranding: boolean;
  prioritySupport: boolean;
  clientPortal?: boolean;
  timeTracking?: boolean;
  projectManagement?: boolean;
  documentManagement?: boolean;
}

export interface Plan {
  _id: string;
  name: string;
  displayName: string;
  description: string;
  cost: number;
  currency: string;
  duration: 'monthly' | 'quarterly' | 'yearly' | 'lifetime';
  durationInDays: number;
  maxUsers: number;
  maxClients: number;
  features: PlanFeature[];
  sequenceNumber: number;
  isActive: boolean;
  isRecommended: boolean;
  stripeProductId?: string;
  stripePriceId?: string;
  featureFlags: FeatureFlags;
  createdAt: string;
  updatedAt: string;
}

export interface PlanFeature {
  name: string;
  description?: string;
  enabled: boolean;
}

export interface CreatePlanData {
  name: string;
  displayName: string;
  description: string;
  cost: number;
  currency: string;
  duration: string;
  maxUsers: number;
  maxClients: number;
  features?: PlanFeature[];
  sequenceNumber?: number;
  featureFlags: FeatureFlags;
}

class PlanService {
  /**
   * Get all plans (SuperAdmin only)
   */
  async getAllPlans(activeOnly: boolean = false): Promise<{ data: Plan[] }> {
    const response = await api.get(`/plans${activeOnly ? '?activeOnly=true' : ''}`);
    return response.data;
  }

  /**
   * Get active plans for purchase (public endpoint - no authentication required)
   */
  async getPlansForPurchase(): Promise<{ data: Plan[] }> {
    const response = await publicApi.get('/plans/purchase/list');
    return response.data;
  }

  /**
   * Get plan by ID
   */
  async getPlanById(planId: string): Promise<Plan> {
    const response = await api.get(`/plans/${planId}`);
    return response.data.data;
  }

  /**
   * Create a new plan
   */
  async createPlan(data: CreatePlanData): Promise<Plan> {
    const response = await api.post('/plans', data);
    return response.data.data;
  }

  /**
   * Update a plan
   */
  async updatePlan(planId: string, data: Partial<CreatePlanData>): Promise<Plan> {
    const response = await api.put(`/plans/${planId}`, data);
    return response.data.data;
  }

  /**
   * Delete a plan
   */
  async deletePlan(planId: string): Promise<void> {
    await api.delete(`/plans/${planId}`);
  }

  /**
   * Toggle plan active status
   */
  async togglePlanStatus(planId: string): Promise<Plan> {
    const response = await api.put(`/plans/${planId}/toggle-status`);
    return response.data.data;
  }

  /**
   * Toggle plan recommended status
   */
  async togglePlanRecommended(planId: string): Promise<Plan> {
    const response = await api.put(`/plans/${planId}/toggle-recommended`);
    return response.data.data;
  }

  /**
   * Update plan sequence numbers
   */
  async updatePlanSequence(
    planSequences: { planId: string; sequenceNumber: number }[]
  ): Promise<void> {
    await api.put('/plans/reorder', { planSequences });
  }

  /**
   * Get active plans for purchase
   */
  async getActivePlansForPurchase(): Promise<Plan[]> {
    const response = await api.get('/plans/purchase/list');
    return response.data.data;
  }

  /**
   * Sync plan with Stripe
   */
  async syncPlanWithStripe(planId: string): Promise<Plan> {
    const response = await api.post(`/plans/${planId}/sync-stripe`);
    return response.data.data;
  }
}

export const planService = new PlanService();
